// ============================================================
// NotificationsPanel.tsx
// Dropdown panel showing the user's Notification feed (AppNotification
// rows from GET /api/me/notifications) — OPTION A of the bell redesign
// (casevo-notifications-bell.html): a surface card grouped TODAY / EARLIER
// (local day), each row a coloured type-icon tile + title / body + relative
// time, unread rows carrying a green left edge + dot + mint tint.
// Clicking a row marks it read + deep-links (handled by the parent's
// onSelect). "Mark all as read" (only when unread) + empty state.
// Closes on outside click or Escape.
// No gold here: every notification is already "yours" (design-language
// gold restraint).
// ============================================================
import React, { useEffect, useRef, useCallback } from 'react'
import type { AppNotification } from '../../types/notification.types'

interface NotificationsPanelProps {
  isOpen: boolean
  onClose: () => void
  notifications: AppNotification[]
  onSelect: (n: AppNotification) => void
  onMarkAllRead: () => void
  hasUnread: boolean
  // Server unread count (header "N new" pill). Falls back to the loaded
  // rows' unread count when omitted.
  unreadCount?: number
  // Optional ref to an element that should NOT count as "outside" for
  // outside-click close (typically the trigger bell).
  excludeRef?: React.RefObject<HTMLElement | null>
}

// Compact relative time: "just now", "5m", "3h", "2d", else a date.
const relativeTime = (iso: string): string => {
  const then = new Date(iso).getTime()
  if (isNaN(then)) return ''
  const diffMs = Date.now() - then
  const min = Math.floor(diffMs / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const days = Math.floor(hr / 24)
  if (days < 7) return `${days}d`
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

// Same LOCAL civil day as now (Today vs Earlier grouping).
const isToday = (iso: string): boolean => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return false
  const n = new Date()
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate()
}

// ── Type → icon + tint (mockup legend) ──────────────────────
// Feather line icons. Tints copy the mockup's --t*-bg / --t*-fg values.
const ICON_PATHS: Record<string, React.ReactNode> = {
  task: <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  case: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>,
  done: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>,
  edit: <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />,
  at: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
}

const TINT = {
  green: 'bg-[#d8f3e8] text-[#2d6a4f] dark:bg-[#1d3b2c] dark:text-[#8fd6b0]',
  red: 'bg-[#fbe9e6] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094]',
  sky: 'bg-[#e5f0fa] text-[#2f6aa3] dark:bg-[rgba(124,174,230,0.15)] dark:text-[#9cc3ef]',
  violet: 'bg-[#efe9f8] text-[#6a4c9c] dark:bg-[rgba(176,148,226,0.16)] dark:text-[#c4adf0]',
  amber: 'bg-[#f8f0dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]',
  neutral: 'bg-[#eef2ef] text-[#5b6b62] dark:bg-[#324438] dark:text-[#a9bdb1]',
} as const

type TypeStyle = { icon: keyof typeof ICON_PATHS; tint: keyof typeof TINT }
// Every backend `type` string (createNotification call sites). 'mention' is
// reserved for a future type — no backend emits it yet.
export const NOTIFICATION_TYPE_STYLE: Record<string, TypeStyle> = {
  task_assigned: { icon: 'task', tint: 'green' },
  task_assignee_added: { icon: 'task', tint: 'green' },
  case_assigned: { icon: 'case', tint: 'green' },
  case_assignee_added: { icon: 'case', tint: 'green' },
  task_completed: { icon: 'done', tint: 'green' },
  deadline_due: { icon: 'clock', tint: 'red' },
  task_due: { icon: 'clock', tint: 'red' },
  appointment_invite: { icon: 'cal', tint: 'sky' },
  day_reminder: { icon: 'bell', tint: 'amber' },
  task_edited: { icon: 'edit', tint: 'neutral' },
  appointment_edited: { icon: 'edit', tint: 'neutral' },
  email_change_request: { icon: 'user', tint: 'neutral' },
  email_changed: { icon: 'user', tint: 'neutral' },
  // Bill-approval workflow (backend lib/billApproval).
  bill_submitted: { icon: 'clock', tint: 'amber' },
  bill_approved: { icon: 'done', tint: 'green' },
  bill_sent_back: { icon: 'edit', tint: 'amber' },
  // The daily "No limitation date" nudge (backend deadlineTaskScan).
  limitation_missing: { icon: 'clock', tint: 'amber' },
  // (D12) E-signature on a settlement statement (backend lib/notifications; linkKind 'statement').
  'esign.signed': { icon: 'done', tint: 'green' },
  'esign.confirm_done': { icon: 'done', tint: 'green' },
  'esign.disputed': { icon: 'edit', tint: 'amber' },
  'esign.paper': { icon: 'edit', tint: 'amber' },
  'esign.declined': { icon: 'edit', tint: 'amber' },
  'esign.expired': { icon: 'clock', tint: 'amber' },
  'esign.confirm_request': { icon: 'user', tint: 'sky' },
  'esign.confirm_refused': { icon: 'user', tint: 'red' },
  'esign.not_me': { icon: 'user', tint: 'red' },
  'esign.locked': { icon: 'clock', tint: 'red' },
  'esign.seal_failed': { icon: 'bell', tint: 'red' },
  'esign.reminder_failed': { icon: 'bell', tint: 'red' },
  mention: { icon: 'at', tint: 'violet' }, // unused — no backend type yet
}
const FALLBACK_STYLE: TypeStyle = { icon: 'bell', tint: 'neutral' }

const Icon: React.FC<{ name: keyof typeof ICON_PATHS; className: string }> = ({ name, className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
    {ICON_PATHS[name]}
  </svg>
)

const GROUP_HEAD = 'text-[10.5px] tracking-[0.16em] uppercase text-casevo-text-soft font-semibold px-[16px] pt-[10px] pb-[4px]'

const Row: React.FC<{ n: AppNotification; onSelect: (n: AppNotification) => void }> = ({ n, onSelect }) => {
  const unread = !n.readAt
  // Unread tint: color-mix, NOT `bg-casevo-mint/20` — casevo-* colours are
  // bare var()s, so Tailwind's /N opacity modifier emits no CSS at all.
  // Link-less rows (e.g. task_edited) aren't navigable: no hover/chevron,
  // cursor-default — but a click still marks them read (as before).
  const clickable = !!(n.linkKind && n.linkId)
  const style = NOTIFICATION_TYPE_STYLE[n.type] ?? FALLBACK_STYLE
  return (
    <button
      type="button"
      onClick={() => onSelect(n)}
      className={`group w-full text-left flex gap-[11px] items-start py-[10px] pr-[16px] pl-[14px] border-l-2 transition-colors focus:outline-none casevo-focus
        ${unread ? 'border-casevo-brand bg-[color-mix(in_srgb,var(--casevo-mint)_35%,transparent)]' : 'border-transparent'}
        ${clickable ? 'hover:bg-casevo-input cursor-pointer' : 'cursor-default'}`}
    >
      <span className={`shrink-0 w-[32px] h-[32px] rounded-[9px] grid place-items-center ${TINT[style.tint]}`}>
        <Icon name={style.icon} className="w-[16px] h-[16px]" />
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block text-[13px] leading-[1.35] line-clamp-2 ${
            unread
              ? 'font-semibold text-casevo-forest'
              : 'text-[color:color-mix(in_srgb,var(--casevo-forest)_82%,var(--casevo-text-soft))]'
          }`}
        >
          {n.title}
        </span>
        {n.body && (
          <span className="block text-[12px] text-casevo-text-soft mt-[2px] truncate">{n.body}</span>
        )}
      </span>
      <span className="shrink-0 flex flex-col items-end gap-[7px] pt-[1px]">
        <span className="text-[11px] text-casevo-text-soft whitespace-nowrap tabular-nums">
          {relativeTime(n.createdAt)}
        </span>
        {unread ? (
          <span className="w-[8px] h-[8px] rounded-full bg-casevo-brand" aria-label="Unread" />
        ) : clickable ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            className="w-[14px] h-[14px] text-casevo-text-soft opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        ) : null}
      </span>
    </button>
  )
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isOpen,
  onClose,
  notifications,
  onSelect,
  onMarkAllRead,
  hasUnread,
  unreadCount,
  excludeRef,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current?.contains(target)) return
      if (excludeRef?.current?.contains(target)) return
      onClose()
    },
    [onClose, excludeRef],
  )
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!isOpen) return
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }, 10)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, handleClickOutside, handleEscape])

  if (!isOpen) return null

  const today = notifications.filter((n) => isToday(n.createdAt))
  const earlier = notifications.filter((n) => !isToday(n.createdAt))
  const newCount = unreadCount ?? notifications.filter((n) => !n.readAt).length

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
      className="fixed top-16 left-4 right-4 max-w-md sm:absolute sm:top-full sm:left-auto sm:right-0 sm:mt-[16px] sm:max-w-none sm:w-[384px]
                 bg-casevo-surface border border-casevo-border rounded-[14px] text-casevo-forest
                 shadow-[0_18px_44px_rgba(10,25,17,0.20),0_2px_6px_rgba(10,25,17,0.08)] dark:shadow-[0_18px_44px_rgba(0,0,0,0.5),0_2px_6px_rgba(0,0,0,0.3)]
                 z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-[8px] px-[16px] pt-[14px] pb-[10px]">
        <h3 className="text-[14px] font-bold tracking-[-0.005em] text-casevo-forest">Notifications</h3>
        {hasUnread && newCount > 0 && (
          <span className="text-[11px] font-bold text-casevo-brand dark:text-[#8fd6b0] bg-casevo-mint rounded-full px-[7px] py-[1px]">
            {newCount} new
          </span>
        )}
        {hasUnread && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="ml-auto inline-flex items-center gap-[5px] text-[12px] font-semibold text-casevo-brand px-[6px] py-[4px] rounded-[6px] hover:bg-casevo-input transition-colors focus:outline-none casevo-focus"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="w-[13px] h-[13px]">
              <polyline points="18 6 7 17 2 12" /><polyline points="22 10 13 19 11 17" />
            </svg>
            Mark all as read
          </button>
        )}
      </div>

      {/* Content */}
      <div className="max-h-[470px] overflow-y-auto border-t border-casevo-border-soft">
        {notifications.length === 0 ? (
          <div className="pt-[34px] px-[24px] pb-[38px] text-center">
            <div className="w-[52px] h-[52px] mx-auto mb-[12px] rounded-[16px] bg-casevo-mint text-casevo-brand dark:text-[#8fd6b0] grid place-items-center">
              <Icon name="bell" className="w-[24px] h-[24px]" />
            </div>
            <p className="text-[14px] font-bold text-casevo-forest">You’re all caught up</p>
            <p className="text-[12.5px] text-casevo-text-soft mt-[4px] leading-[1.45]">
              New tasks, deadlines, invites and case updates<br />assigned to you will appear here.
            </p>
          </div>
        ) : (
          <>
            {today.length > 0 && (
              <>
                <div className={GROUP_HEAD}>Today</div>
                {today.map((n) => <Row key={n.id} n={n} onSelect={onSelect} />)}
              </>
            )}
            {earlier.length > 0 && (
              <>
                <div className={GROUP_HEAD}>Earlier</div>
                {earlier.map((n) => <Row key={n.id} n={n} onSelect={onSelect} />)}
              </>
            )}
          </>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="border-t border-casevo-border-soft px-[16px] py-[9px] text-[11.5px] text-casevo-text-soft">
          Latest 20 · updates automatically
        </div>
      )}
    </div>
  )
}

export default NotificationsPanel
